// useAudioManager Hook - Manages all audio recording and upload functionality
import { useState, useRef, useCallback, useEffect } from 'react';
import { getSignedUrlForS3Path } from '../utils/s3PathUtils';

// File size limits configuration
const FILE_LIMITS = {
  audio: {
    extensions: ['.mp3', '.wav', '.ogg', '.m4a', '.webm'],
    maxSize: 20 * 1024 * 1024,
    label: '20MB'
  },
  video: {
    extensions: ['.mov'],
    maxSize: 500 * 1024 * 1024,
    label: '500MB'
  }
};

const useAudioManager = (existingAudioData = null, userId = null) => {
  // Core audio state
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioSource, setAudioSource] = useState(null); // 'recording' | 'upload' | 'existing'

  // Upload-specific state
  const [uploadedAudioFile, setUploadedAudioFile] = useState(null);
  const [uploadedAudioName, setUploadedAudioName] = useState('');
  const [uploadedAudioSize, setUploadedAudioSize] = useState(0);

  // Existing audio state
  const [existingVoiceUrl, setExistingVoiceUrl] = useState(null);
  const [signedVoiceUrl, setSignedVoiceUrl] = useState(null);
  const [replacingAudio, setReplacingAudio] = useState(false);

  // Error handling
  const [audioError, setAudioError] = useState(null);

  // Refs for recording
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Initialize existing audio data
  useEffect(() => {
    console.log('[AUDIO MANAGER] Initializing with existingAudioData:', existingAudioData);
    console.log('[AUDIO MANAGER] userId:', userId);

    if (existingAudioData) {
      const { voiceS3Url, fileDuration, voiceFileName } = existingAudioData;

      // Check if voiceS3Url is a valid S3 URL (not a pending-upload placeholder)
      if (voiceS3Url && !voiceS3Url.startsWith('pending-upload-')) {
        console.log('[AUDIO MANAGER] Loading existing audio:', voiceS3Url);
        console.log('[AUDIO MANAGER] File duration:', fileDuration);
        console.log('[AUDIO MANAGER] File name:', voiceFileName);

        setExistingVoiceUrl(voiceS3Url);
        setAudioURL(voiceS3Url);
        setAudioSource('existing');

        if (fileDuration) {
          setRecordingDuration(fileDuration);
        }

        if (voiceFileName) {
          setUploadedAudioName(voiceFileName);
        } else {
          const fileName = voiceS3Url.split('/').pop().split('?')[0];
          setUploadedAudioName(fileName);
        }
      } else if (voiceS3Url && voiceS3Url.startsWith('pending-upload-')) {
        console.log('[AUDIO MANAGER] Pending upload detected, not loading as existing audio:', voiceS3Url);
        // Don't set any audio URL or source for pending uploads
        // The audio will be re-fetched once it's properly uploaded
      } else {
        console.log('[AUDIO MANAGER] No voiceS3Url in existingAudioData');
      }
    } else {
      console.log('[AUDIO MANAGER] No existingAudioData provided');
    }
  }, [existingAudioData, userId]);

  // Fetch signed URL for existing audio
  useEffect(() => {
    const fetchSignedUrl = async () => {
      // Only fetch signed URL if we have a valid S3 URL (not a pending-upload placeholder)
      if (existingVoiceUrl && userId && !existingVoiceUrl.startsWith('pending-upload-')) {
        try {
          console.log('[AUDIO MANAGER] Fetching signed URL for audio...');
          const signedUrl = await getSignedUrlForS3Path(existingVoiceUrl, userId);
          console.log('[AUDIO MANAGER] Got signed URL');
          setSignedVoiceUrl(signedUrl);
          setAudioError(null);
        } catch (error) {
          console.error('[AUDIO MANAGER] Failed to get signed URL:', error);
          setAudioError('Unable to load audio file. Please try again.');
        }
      } else if (existingVoiceUrl && existingVoiceUrl.startsWith('pending-upload-')) {
        console.log('[AUDIO MANAGER] Skipping signed URL fetch for pending upload:', existingVoiceUrl);
      }
    };

    fetchSignedUrl();
  }, [existingVoiceUrl, userId]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioURL(url);
        setAudioSource('recording');

        // Clear replacing flag when we have new audio
        setReplacingAudio(false);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      setAudioError('Could not access microphone. Please check permissions.');
    }
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [isRecording]);

  // Delete recording
  const deleteAudio = useCallback(() => {
    // Revoke object URL if it exists
    if (audioURL && audioURL.startsWith('blob:')) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingDuration(0);
    setAudioSource(null);
    setUploadedAudioFile(null);
    setUploadedAudioName('');
    setUploadedAudioSize(0);
  }, [audioURL]);

  // Replace existing audio
  const replaceAudio = useCallback(() => {
    console.log('[AUDIO MANAGER] Replace audio called - clearing all audio state');
    // Clear all audio state to enable recording
    if (audioURL && audioURL.startsWith('blob:')) {
      URL.revokeObjectURL(audioURL);
    }
    setReplacingAudio(true);
    setAudioURL(null);
    setAudioBlob(null);
    setAudioSource(null);
    setRecordingDuration(0);
    setUploadedAudioFile(null);
    setUploadedAudioName('');
    setUploadedAudioSize(0);
    setAudioError(null);
  }, [audioURL]);

  // Cancel replace
  const cancelReplace = useCallback(() => {
    setReplacingAudio(false);
    if (existingVoiceUrl) {
      setAudioURL(existingVoiceUrl);
      setAudioSource('existing');
    }
  }, [existingVoiceUrl]);

  // Handle file upload
  const handleAudioUpload = useCallback((file, url, duration) => {
    console.log('[AUDIO MANAGER] Audio file uploaded:', file.name, 'Size:', file.size);
    setAudioBlob(file);
    setAudioURL(url);
    setRecordingDuration(Math.floor(duration));
    setAudioSource('upload');
    setUploadedAudioFile(file);
    setUploadedAudioName(file.name);
    setUploadedAudioSize(file.size);
    setAudioError(null);
    setReplacingAudio(false); // Clear replacing state
  }, []);

  // Format duration helper
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Format file size helper
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Helper function to get file type config
  const getFileTypeConfig = useCallback((fileName) => {
    const extension = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
    if (FILE_LIMITS.video.extensions.includes(extension)) {
      return FILE_LIMITS.video;
    }
    return FILE_LIMITS.audio;
  }, []);

  // Return the complete API
  return {
    // State
    state: {
      isRecording,
      audioURL,
      audioBlob,
      recordingDuration,
      audioSource,
      uploadedAudioFile,
      uploadedAudioName,
      uploadedAudioSize,
      existingVoiceUrl,
      signedVoiceUrl,
      replacingAudio,
      audioError
    },

    // Actions
    actions: {
      startRecording,
      stopRecording,
      deleteAudio,
      replaceAudio,
      cancelReplace,
      handleAudioUpload,
      setAudioError
    },

    // Helpers
    helpers: {
      formatDuration,
      formatFileSize,
      getFileTypeConfig,
      FILE_LIMITS
    },

    // Computed properties
    computed: {
      hasAudio: !!audioURL,
      hasNewAudio: !!audioBlob,
      canRecord: !audioURL || replacingAudio,
      isExistingAudio: audioSource === 'existing' && !replacingAudio,
      audioDisplayUrl: signedVoiceUrl || audioURL
    }
  };
};

export default useAudioManager;
